import React, { useRef } from 'react';
import {
  getDoctorChatTicketLabel,
  lang,
  number,
  rgba,
  scssVariables as variables,
  userFullName,
  useScreenDetail
} from 'src/helpers';

import { Avatar, MCButton, MCText, View } from 'src/components/basic';
import { ActivityLabel } from 'src/components/user/LastActivity';
import { remCss, remFontCss } from '../utils';
import moment from 'moment/moment';
import LANG_CONST from 'src/helpers/lang/const';

export const DoctorHeader = ({ doctor, onSendMsg, onBook }) => {
  const screen = useScreenDetail();
  const ref = useRef(null);
  const chatTicketLabel = getDoctorChatTicketLabel(doctor.charge?.chatTicket, doctor.currency);
  const minScreen = 1300;
  return (
    <View
      ref={ref}
      style={{
        flexDirection: 'row',
        gap: remCss(25),
        alignItems: 'center',
        paddingLeft: remCss(30)
      }}
    >
      <Avatar
        text={doctor.firstName}
        width={150}
        height={150}
        image={doctor.avatar}
        onlineState={doctor.connections > 0 ? 'online' : ''}
      />
      <View style={{ flex: 1, gap: remCss(10) }}>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: remCss(15) }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              ...(screen.innerWidth < minScreen
                ? { width: '100%' }
                : {
                    width: '40%',
                    maxWidth: remCss(400)
                  })
            }}
          >
            <View style={{ flex: 1, gap: remCss(10) }}>
              <MCText
                label={userFullName(doctor, true)}
                size={26}
                remFontCss={remFontCss}
                weight={600}
                flex
              />
              <MCText
                size={20}
                remFontCss={remFontCss}
                weight={400}
                textLight
                label={LANG_CONST.constants.doctor_types[doctor.doctorType]}
              />
            </View>
          </View>
          <View style={{ flex: 1, flexDirection: 'row', gap: remCss(15), flexWrap: 'wrap' }}>
            <MCButton
              onClick={(e) => {
                onSendMsg(e);
                const element = ref.current;
                if (element) {
                  window.scrollTo({
                    top: element.getBoundingClientRect().top + window.scrollY - 50
                  });
                }
              }}
              primary
              largeMedium
              dark
              radius
              label={LANG_CONST.buttons.send_a_message}
            />
            <View style={{ flex: 1 }} />
            <MCButton
              label={LANG_CONST.buttons.make_appointment}
              primary
              largeMedium
              dark
              radius
              onClick={onBook}
            />
          </View>
        </View>
        <View
          style={
            screen.innerWidth < minScreen
              ? {}
              : { flexDirection: 'row', gap: remCss(30), alignItems: 'center' }
          }
        >
          <MCText
            data-testid={'public-doctor-profile-status'}
            size={18}
            remFontCss={remFontCss}
            numberOfLines={1}
            weight={400}
            primary
          >
            <ActivityLabel
              accountState={doctor.accountState}
              lastActivity={doctor.lastActivity}
              connections={doctor.connections}
            />
          </MCText>
          {chatTicketLabel && (
            <MCText label={chatTicketLabel} size={20} remFontCss={remFontCss} weight={700} />
          )}
        </View>
      </View>
    </View>
  );
};

export const DoctorDetail = ({
  doctor,
  hospitalName,
  locations = [],
  selectedHosMap,
  setSelectedHosMap,
  medium
}) => {
  let experience = doctor.yearOfStarting
    ? moment().year() - doctor.yearOfStarting + 1
    : doctor.training;
  if (experience > 0) {
    experience = lang.t(experience > 1 ? LANG_CONST.amount_years : LANG_CONST.amount_year, {
      amount: experience
    });
  }

  return (
    <View
      style={{
        flex: 1,
        gap: remCss(40),
        height: medium ? 'auto' : '100%',
        paddingHorizontal: remCss(30)
      }}
    >
      <View style={{ gap: remCss(10) }}>
        <MCText label={LANG_CONST.speciality} size={18} weight={400} textLight />
        <View
          style={{
            flexDirection: 'row',
            gap: remCss(10),
            flexWrap: 'wrap'
          }}
        >
          {doctor.specialist?.map(
            (s) =>
              LANG_CONST.constants.specialist[s] && (
                <MCButton key={s} shadow rounded>
                  <MCText
                    size={20}
                    remFontCss={remFontCss}
                    weight={400}
                    heading
                    label={LANG_CONST.constants.specialist[s]}
                  />
                </MCButton>
              )
          )}
        </View>
      </View>

      {doctor.subSpeciality && (
        <View style={{ gap: remCss(10) }}>
          <MCText
            label={LANG_CONST.sub_speciality}
            size={18}
            remFontCss={remFontCss}
            weight={400}
            textLight
          />
          <View
            style={{
              flexDirection: 'row'
            }}
          >
            <MCButton shadow rounded>
              <MCText
                label={doctor.subSpeciality}
                size={20}
                remFontCss={remFontCss}
                weight={400}
                heading
              />
            </MCButton>
          </View>
        </View>
      )}

      <View style={{ gap: remCss(10) }}>
        <MCText
          label={LANG_CONST.total_experience}
          size={18}
          remFontCss={remFontCss}
          weight={400}
          textLight
        />
        <MCText label={experience} size={20} remFontCss={remFontCss} weight={500} />
      </View>
      {doctor.description && (
        <View style={{ gap: remCss(10) }}>
          <MCText
            label={LANG_CONST.about_me}
            size={18}
            remFontCss={remFontCss}
            weight={400}
            textLight
          />
          <MCText label={doctor.description} size={20} remFontCss={remFontCss} weight={500} />
        </View>
      )}

      {(hospitalName || !!locations?.length) && (
        <View style={{ gap: remCss(20) }}>
          <MCText label={LANG_CONST.work_location} size={22} remFontCss={remFontCss} weight={600} />
          {hospitalName && (
            <View style={{ gap: remCss(15) }}>
              <MCText
                label={LANG_CONST.hospital_name}
                size={18}
                remFontCss={remFontCss}
                weight={400}
                textLight
              />
              <MCText label={hospitalName} size={18} remFontCss={remFontCss} weight={500} />
            </View>
          )}
          {!!locations?.length && (
            <View style={{ gap: remCss(15) }}>
              <MCText
                label={LANG_CONST.location}
                size={18}
                remFontCss={remFontCss}
                weight={400}
                textLight
              />
              {locations.map((h) => {
                return (
                  <MCText
                    key={h.id}
                    style={{
                      cursor: 'pointer',
                      ...(selectedHosMap?.id === h.id
                        ? {
                            backgroundColor: rgba(variables['colors-mc-primary'], 0.1),
                            padding: remCss(10),
                            radius: remCss(3)
                          }
                        : {
                            paddingHorizontal: remCss(10)
                          })
                    }}
                    onClick={() => setSelectedHosMap(h)}
                    label={h.hospital?.split('_')[0]}
                    size={18}
                    remFontCss={remFontCss}
                    weight={500}
                    heading
                  />
                );
              })}
            </View>
          )}
        </View>
      )}

      {!!doctor.treatments?.length && (
        <View style={{ gap: remCss(20) }}>
          <MCText
            label={LANG_CONST.titles.treatments}
            size={22}
            weight={600}
            style={{ marginTop: remCss(5) }}
          />
          <View style={{ gap: remCss(15) }}>
            {doctor.treatments?.map((item, i) => (
              <View key={i} style={{ flexDirection: 'row', gap: remCss(15), alignItems: 'center' }}>
                <MCText
                  label={item.name}
                  size={18}
                  remFontCss={remFontCss}
                  weight={400}
                  textLight
                  flex
                />
                <MCText
                  label={'$' + number.format(item.fee || 0)}

                  size={18}
                  remFontCss={remFontCss}
                  weight={500}
                />
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};
